import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

/**
 * new: white on the button orange (a NEW lesson, a new leaderboard entry).
 * quiz: the one interactive pill, a button (peach, dark orange text).
 * score, premium, mastered, done, gold: the soft gold family.
 * accent: peach with dark orange text, not interactive (the "You" marker).
 * success, danger, neutral: an outcome (a confirmed, failed or expired
 * wallet transaction): green only for confirmed, danger only for a failure.
 */
export type PillVariant =
  | 'new'
  | 'quiz'
  | 'score'
  | 'premium'
  | 'mastered'
  | 'done'
  | 'gold'
  | 'accent'
  | 'success'
  | 'danger'
  | 'neutral';

interface PillBase {
  variant: PillVariant;
  className?: string;
  children?: ReactNode;
}

export type PillAsSpanProps = PillBase &
  Omit<HTMLAttributes<HTMLSpanElement>, 'className' | 'children'> & { as?: 'span' };

export type PillAsButtonProps = PillBase &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children' | 'type'> & {
    as: 'button';
  };

export type PillProps = PillAsSpanProps | PillAsButtonProps;

/**
 * A small rounded label (redesign PR6, promoted from the Course page). Text
 * on every variant holds 4.5:1 (design-system/__tests__/pill-contrast.test.ts).
 * A leading Lucide icon is sized by Pill.css; mark it aria-hidden.
 */
export function Pill(props: PillProps) {
  const classes = cx('ee-pill', `ee-pill--${props.variant}`, props.className);
  if (props.as === 'button') {
    const { as: _as, variant: _variant, className: _className, children, ...rest } = props;
    return (
      <button type="button" {...rest} className={classes}>
        {children}
      </button>
    );
  }
  const { as: _as, variant: _variant, className: _className, children, ...rest } = props;
  return (
    <span {...rest} className={classes}>
      {children}
    </span>
  );
}
